import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { Router } from '@angular/router';
import { PwaInstructionsComponent } from '../shared-components/pwa-instructions/pwa-instructions.component';
import { PwaService } from '../../service/pwa.service';

/**
 * InstallHelpComponent — the public `/install-help` route.
 *
 * A link someone can be sent before they have anything: the route is registered
 * with NO MsalGuard or RoleGuard (see app-routing.module.ts), because the two
 * people who need it are exactly the two who cannot get past a login — someone
 * installing the app for the first time, and a desktop user reading the steps in
 * a browser they have never signed into. Nothing here touches the API or the
 * signed-in user, so there is no data to protect by guarding it.
 *
 * It renders its own ion-header rather than sitting under the app's shell: the
 * shell's toolbar carries the location selector, menu button and login state,
 * none of which mean anything to an unauthenticated visitor. AppComponent hides
 * that shell on this route (see `isStandalonePage`), so this page owns the frame.
 *
 * The instructions themselves are {@link PwaInstructionsComponent}, shared with
 * {@link PwaInstructionsModalComponent} — this component adds only the header, a
 * way back into the app, and the "you're already running it" case.
 */
@Component({
  selector: 'app-install-help',
  standalone: true,
  imports: [CommonModule, IonicModule, PwaInstructionsComponent],
  templateUrl: './install-help.component.html',
  styleUrls: ['./install-help.component.css'],
})
export class InstallHelpComponent {
  /**
   * True when this page is being viewed from the already-installed app, where
   * the steps have nothing to offer. The instructions stay reachable below the
   * notice rather than being hidden — someone may well have opened the link to
   * help a colleague install it on their phone.
   */
  public readonly alreadyInstalled: boolean;

  constructor(
    private _router: Router,
    pwaService: PwaService,
  ) {
    this.alreadyInstalled = pwaService.isRunningStandalone();
  }

  /**
   * Sends the visitor to the landing page, where they can sign in. This is the
   * only way off this page: it has no menu, because the shell that holds the menu
   * is hidden here.
   */
  public goToApp(): void {
    this._router.navigate(['/']);
  }
}
